import React, { useState } from 'react';
import { Phone, Send, CheckCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';
import { toast } from 'sonner';
import { businessInfo, services } from '../../data/mock';
import { submitLead } from '../../lib/leads';

const initialForm = { name: '', phone: '', email: '', service: '', message: '' };

const QuoteDialog = ({ open, onOpenChange, defaultService = '' }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleClose = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      // Reset shortly after close animation
      setTimeout(() => { setForm(initialForm); setSuccess(false); }, 250);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please enter your name and phone number.');
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        ...form,
        service: form.service || defaultService || '',
        source: 'quote_dialog',
        page: typeof window !== 'undefined' ? window.location.pathname : '',
      });
      setSuccess(true);
      toast.success("Request received! We'll call you back shortly.");
    } catch (err) {
      toast.error('Something went wrong. Please call us at ' + businessInfo.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="quote-dialog">
        {success ? (
          <div className="py-6 text-center" data-testid="quote-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-[#1a3a5c] mb-2">Request Received!</h3>
            <p className="text-gray-600 mb-6">
              Thanks, {form.name.split(' ')[0] || 'there'}. A Basin Plumbing team member will reach out shortly.
              Need help now? Call us 24/7.
            </p>
            <a href={`tel:${businessInfo.phone}`}>
              <Button className="bg-[#e85a24] hover:bg-[#d14a14] text-white" data-testid="quote-success-call">
                <Phone size={18} className="mr-2" />
                {businessInfo.phone}
              </Button>
            </a>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1a3a5c]">Get a Free Quote</DialogTitle>
              <DialogDescription>
                Tell us what you need and we'll get back to you fast. Serving Odessa, Midland &amp; the Permian Basin.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="q-name">Name *</Label>
                <Input id="q-name" name="name" value={form.name} onChange={handleChange} placeholder="Your name" data-testid="quote-name-input" required />
              </div>
              <div>
                <Label htmlFor="q-phone">Phone *</Label>
                <Input id="q-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(432) 555-0123" data-testid="quote-phone-input" required />
              </div>
              <div>
                <Label htmlFor="q-email">Email</Label>
                <Input id="q-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@email.com" data-testid="quote-email-input" />
              </div>
              <div>
                <Label htmlFor="q-service">Service Needed</Label>
                <Select value={form.service || defaultService} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger id="q-service" data-testid="quote-service-select">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Emergency Plumbing">Emergency Plumbing</SelectItem>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="q-message">How can we help?</Label>
                <Textarea id="q-message" name="message" value={form.message} onChange={handleChange} placeholder="Describe your plumbing issue..." rows={3} data-testid="quote-message-input" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#e85a24] hover:bg-[#d14a14] text-white py-6 text-base" data-testid="quote-submit-button">
                {submitting ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Send size={18} className="mr-2" /> Request My Free Quote</>
                )}
              </Button>
              <p className="text-center text-xs text-gray-500">
                Prefer to talk? Call <a href={`tel:${businessInfo.phone}`} className="text-[#e85a24] font-medium">{businessInfo.phone}</a> — available 24/7.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
